import {generatePeaksFromAudioSrcUrl} from '@/utils/generate_waveform_data';
import {isAudioFile} from '@/utils/file_utils';

import {AttributeName, AttributeParentType, DB} from '@/store/kysely_types';
import {Kysely} from 'kysely';
import {generateId} from '@/store/utils/generate_id';
import {DBObjects} from '@/store/store_types';

type Props = {
    db: Kysely<DB>;
    getFileSrc: (file: DBObjects['File'] & {full_path?: string}) => Promise<string>;
    decodeAudioData: (arrayBuffer: ArrayBuffer) => Promise<AudioBuffer>;
}

export default async function (props: Props) {
    await runMigration(props);
}

const runMigration = async ({db, getFileSrc, decodeAudioData}: Props) => {
    const workspaces = await getAllWorkspaces({db});

    for (const workspace of workspaces) {
        const files = await getFilesForWorkspace({db, workspace_id: workspace.id});

        for (const file of files) {
            const existingWaveform = await getFileWaveformAndDuration({db, file_id: file.id});
            if (existingWaveform) {
                continue;
            }

            if (isAudioFile(file.name)) {
                try {
                    const fileSrc = await getFileSrc(file);
                    const waveform = await generatePeaksFromAudioSrcUrl(fileSrc, 1000, decodeAudioData);
                    await updateObjectAttribute({
                        db,
                        created_by: file.created_by,
                        parent_id: file.id,
                        parent_type: 'File',
                        attribute: 'waveform_data',
                        value: JSON.stringify(waveform.peaks),
                    });
                    await updateObjectAttribute({
                        db,
                        created_by: file.created_by,
                        parent_id: file.id,
                        parent_type: 'File',
                        attribute: 'duration',
                        value: Math.floor(waveform.duration).toString(),
                    });
                } catch (e) {
                    console.error('Failed to generate waveform for file', file, e);
                }
            }
        }
    }
};

const getAllWorkspaces = async (args: {
    db: Kysely<DB>;
}) => {
    const db = args.db;

    const workspaces = await db.selectFrom('Workspace')
        .selectAll('Workspace')
        .execute();

    return workspaces;
};

const getFilesForWorkspace = async (args: {
    db: Kysely<DB>;
    workspace_id: string;
}) => {
    const db = args.db;

    const files = await db.selectFrom('File')
        .innerJoin('RawFile', 'RawFile.file_id', 'File.id')
        .selectAll('File')
        .select('RawFile.full_path')
        .where('File.workspace_id', '=', args.workspace_id)
        .execute();

    return files;
};

const getFileWaveformAndDuration = async (args: {
    db: Kysely<DB>;
    file_id: string;
}) => {
    const db = args.db;

    const waveform = await db.selectFrom('Attribute')
        .select('Attribute.value')
        .where('Attribute.parent_id', '=', args.file_id)
        .where('Attribute.parent_type', '=', 'File')
        .where('Attribute.name', '=', 'waveform_data')
        .executeTakeFirst();

    return waveform;
};

const updateObjectAttribute = async (args: {
    parent_type: AttributeParentType;
    parent_id: string;
    attribute: AttributeName;
    value: string;
    db: Kysely<DB>;
    created_by: string;
}) => {
    const db = args.db;

    const parentObject = await db.selectFrom(args.parent_type as 'File').select('workspace_id')
        .where('id', '=', args.parent_id)
        .executeTakeFirstOrThrow();

    if (!parentObject) {
        throw new Error('Parent object not found with id ' + args.parent_id);
    }

    // if (!await checkWorkspaceMembership(this.moduleAPI, parentObject)) {
    //     throw new Error('Not a member of workspace');
    // }

    // const userContext = await getCurrentUserContext(this.moduleAPI);

    const attributeRows = await db.selectFrom('Attribute').selectAll()
        .where('Attribute.parent_type', '=', args.parent_type)
        .where('Attribute.parent_id', '=', args.parent_id)
        .where('Attribute.name', '=', args.attribute)
        .execute();

    if (attributeRows.length > 0) {
        await db.updateTable('Attribute').set({
            value: args.value,
            updated_at: new Date().toISOString(),
        }).where('Attribute.id', '=', attributeRows[0].id).execute();
    } else {
        await db.insertInto('Attribute').values({
            id: generateId(),
            name: args.attribute,
            value: args.value,
            parent_type: args.parent_type,
            parent_id: args.parent_id,
            workspace_id: parentObject.workspace_id,
            created_by: args.created_by,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        }).execute();
    }
};
